import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { technologyCategories } from '../data/technologies';
import { Section, Card } from '../components/common';

const TechnologiesContainer = styled(Section)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, #1a1a2e 50%, ${({ theme }) => theme.colors.background} 100%);
  position: relative;
`;

const TechnologiesContent = styled.div`
  .section-title {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.navyBlue}, ${({ theme }) => theme.colors.lightNavy});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-align: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['3xl']};
    }
  }

  .section-subtitle {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
    text-align: center;
    max-width: 600px;
    margin: 0 auto ${({ theme }) => theme.spacing['3xl']};
    line-height: 1.7;
  }
`;

const TechShowcase = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: row;
    overflow-x: auto;
    gap: ${({ theme }) => theme.spacing.md};
    padding-bottom: ${({ theme }) => theme.spacing.sm};
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.navyBlue : theme.colors.surface};
  color: ${({ $active, theme }) =>
    $active ? 'white' : theme.colors.textPrimary};
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.colors.navyBlue : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-align: left;
  
  /* Better touch targets */
  min-height: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.navyBlue};
    transform: translateX(4px);
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      transform: scale(0.98);
      background: ${({ $active, theme }) =>
        $active ? theme.colors.lightNavy : 'rgba(30, 58, 138, 0.1)'};
    }
    
    &:hover {
      transform: none; /* Disable hover transform on touch devices */
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-shrink: 0;
    min-width: 160px;
    text-align: center;
    
    .category-description {
      display: none; /* Hide description on tablet */
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 140px;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }

  .category-name {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  .category-description {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    opacity: 0.8;
    font-weight: ${({ theme }) => theme.fontWeights.normal};
  }
`;

const FeaturedTech = styled(Card)`
  padding: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  position: relative;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.surface}, #1f1f2e);
  border: 1px solid ${({ theme }) => theme.colors.navyBlue};
  animation: shimmer 3s ease-in-out infinite;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }

  @keyframes shimmer {
    0%, 100% { border-color: rgba(30, 58, 138, 0.3); }
    50% { border-color: rgba(30, 58, 138, 0.8); }
  }

  .tech-icon {
    font-size: ${({ theme }) => theme.fontSizes['5xl']};
    color: ${({ theme }) => theme.colors.navyBlue};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    animation: float 4s ease-in-out infinite;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['4xl']};
      margin-bottom: ${({ theme }) => theme.spacing.md};
    }
  }

  .tech-name {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }

  .tech-description {
    font-size: ${({ theme }) => theme.fontSizes.base};
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.6;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.sm};
      margin-bottom: ${({ theme }) => theme.spacing.md};
    }
  }

  .experience-level {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.sm};

    .level-label {
      font-size: ${({ theme }) => theme.fontSizes.sm};
      color: ${({ theme }) => theme.colors.textMuted};
      text-transform: uppercase;
      letter-spacing: 1px;
      
      @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        font-size: ${({ theme }) => theme.fontSizes.xs};
        letter-spacing: 0.5px;
      }
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-15px); }
  }
  
  /* Reduce animation on mobile for better performance */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    animation: none; /* Disable shimmer on mobile */
    
    .tech-icon {
      animation: none; /* Disable float on mobile */
    }
  }
`;

const ExperienceDots = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ExperienceDot = styled.div<{ $filled: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $filled, theme }) =>
    $filled ? theme.colors.navyBlue : theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.normal};
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.xl};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const TechCard = styled(Card)<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  border-color: ${({ $active, theme }) =>
    $active ? theme.colors.navyBlue : theme.colors.border};
  background: ${({ $active, theme }) =>
    $active ? 'rgba(30, 58, 138, 0.1)' : theme.colors.surface};
  
  /* Better touch targets */
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.navyBlue};
    box-shadow: 0 8px 25px rgba(30, 58, 138, 0.2);
  }
  
  /* Touch feedback for mobile */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      transform: scale(0.95);
      background: ${({ $active, theme }) =>
        $active ? 'rgba(30, 58, 138, 0.2)' : 'rgba(30, 58, 138, 0.1)'};
    }
    
    &:hover {
      transform: none; /* Disable hover transform on touch devices */
      box-shadow: none;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
    min-height: 70px;
  }

  .tech-icon {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.navyBlue : theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    transition: all ${({ theme }) => theme.transitions.normal};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
      margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
  }

  .tech-name {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.textPrimary};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.xs};
    }
  }
`;

const getLevelText = (level: number): string => {
  switch (level) {
    case 1: return 'Beginner';
    case 2: return 'Intermediate';
    case 3: return 'Advanced';
    case 4: return 'Expert';
    default: return 'Beginner';
  }
};

export const TechnologiesSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('backend');
  const [featuredTech, setFeaturedTech] = useState<number>(0);

  const activeCategories = technologyCategories.find(cat => cat.id === activeCategory);
  const activeTechnologies = activeCategories?.technologies || [];

  // Auto-rotate featured technology
  useEffect(() => {
    if (activeTechnologies.length === 0) return;

    const interval = setInterval(() => {
      setFeaturedTech(prev => (prev + 1) % activeTechnologies.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [activeTechnologies.length]);

  // Reset featured tech when category changes
  useEffect(() => {
    setFeaturedTech(0);
  }, [activeCategory]);

  const renderExperienceDots = (level: number) => {
    return (
      <ExperienceDots>
        {Array.from({ length: 4 }, (_, i) => (
          <ExperienceDot key={i} $filled={i < level} />
        ))}
      </ExperienceDots>
    );
  };

  const currentTech = activeTechnologies[featuredTech];

  return (
    <TechnologiesContainer id="technologies">
        <TechnologiesContent>
          <h2 className="section-title">Technologies & Skills</h2>
          <p className="section-subtitle">
            A showcase of the technologies I work with, from backend systems to data analytics.
          </p>

          <TechShowcase>
            <CategoryTabs>
              {technologyCategories.map((category) => (
                <CategoryTab
                  key={category.id}
                  $active={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <span className="category-name">{category.name}</span>
                  <span className="category-description">{category.description}</span>
                </CategoryTab>
              ))}
            </CategoryTabs>

            {currentTech && (
              <FeaturedTech>
                {React.createElement(currentTech.icon as any, { className: "tech-icon" })}
                <h3 className="tech-name">{currentTech.name}</h3>
                <p className="tech-description">{currentTech.description}</p>
                <div className="experience-level">
                  <span className="level-label">Experience</span>
                  {renderExperienceDots(currentTech.level)}
                  <span className="level-label">{getLevelText(currentTech.level)}</span>
                </div>
              </FeaturedTech>
            )}
          </TechShowcase>

          <TechGrid>
            {activeTechnologies.map((tech, index) => (
              <TechCard
                key={tech.name}
                $active={index === featuredTech}
                onClick={() => setFeaturedTech(index)}
                hover
              >
                {React.createElement(tech.icon as any, { className: "tech-icon" })}
                <div className="tech-name">{tech.name}</div>
              </TechCard>
            ))}
          </TechGrid>
        </TechnologiesContent>
    </TechnologiesContainer>
  );
}; 